import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Divider,
  Paper,
  Stack,
  Chip,
  IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadModal from './UploadModal.tsx';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router';

interface DocumentItemProps {
  title: string;
  description: string;
  required?: boolean;
  format: string;
  onClick: () => void;
}


const DocumentItem: React.FC<DocumentItemProps> = ({ title, description, required, format, onClick }) => (

<Paper elevation={2} sx={{ p: 2, mb: 2 }}>
  <Grid container spacing={2}>
    <Grid item xs={12}>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1">{title}</Typography>
        {required && (
          <Chip label="Required" color="error" size="small" />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {description}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Format: {format}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={onClick}
          size="small"
        >
          Upload
        </Button>
      </Box>
    </Grid>
  </Grid>
</Paper>




);


const UpdateDocument: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState('');
  const navigate = useNavigate();

  const handleOpenModal = (docTitle: string) => {
    setActiveDoc(docTitle);
    setModalOpen(true);
  };

  const handleUpload = (file: File) => {
    console.log(`Uploading ${activeDoc}:`, file);
    // You can handle the API upload here
  };

  const breadcrumbsData = [
    {
      label: <IconButton color="primary"><HomeIcon /></IconButton>,
      onClick: () => navigate('/')
    },
    { label: 'Update Document' }
  ]


  const documentsData = [
    {
      sectionTitle: 'Documents List for Firm / Company as per VRF',
      documents: [
        {
          title: "Vendor Registration Form - VRF",
          description: "A completed Vendor Registration Form (VRF) is required to initiate the onboarding process...",
          required: true,
          format: "PDF or Image"
        },
        {
          title: "Vendor / Business Entity name",
          description: "Submit an official document that clearly states the legal name of your business...",
          required: true,
          format: "PDF or Image"
        },
        {
          title: "Business Alias name - If any",
          description: "Provide documentation that supports any alternate or trading names your business operates under...",
          format: "Image (JPG, PNG)"
        },
        {
          title: "PAN No of the Business entity",
          description: "Upload a clear copy of the Permanent Account Number (PAN) card issued to the business entity. This is required for tax identification and compliance purposes.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Business establishment Certificate / Certificate of Incorporation",
          description: "Upload the official certificate that confirms the legal establishment of your business. This may include a Certificate of Incorporation, Shop and Establishment Certificate, or any equivalent government-issued registration proof.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "GST Certificate",
          description: "Upload a copy of your Goods and Services Tax (GST) registration certificate issued by the government. This document is required to verify your business’s tax registration status and GSTIN.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Bank Details - Cancelled Cheque of business entity",
          description: "Upload a scanned copy of a cancelled cheque issued from the business entity’s bank account. This helps verify the account holder's name, account number, and IFSC code for payment processing.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Company Registered Address",
          description: "Provide a document that confirms the officially registered address of your business entity. Accepted documents include utility bills, lease agreements, property tax receipts, or government-issued registration certificates.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "PF / ESI / PT Registration Certificate",
          description: "Upload valid registration certificates for Provident Fund (PF), Employees’ State Insurance (ESI), and Professional Tax (PT), if applicable. These documents confirm your business’s compliance with statutory labor and tax regulations.",
          format: "Image (JPG, PNG)"
        },
      ]
    },
    {
      sectionTitle: 'Documents List for Proprietorship as per VRF',
      documents: [
        {
          title: "Vendor Registration Form - VRF",
          description: "Upload the completed Vendor Registration Form (VRF) containing key information about your proprietorship, such as the proprietor’s name, business address, contact details, and nature of business. This form is essential to initiate vendor onboarding.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Trade Licence",
          description: "Upload a valid Trade Licence issued by the local municipal authority...",
          format: "Image (JPG, PNG)"
        },
        {
          title: "PAN No of the Proprietorship",
          description: "Upload a clear copy of the PAN card issued in the proprietor's name...",
          format: "Image (JPG, PNG)"
        },

        {
          title: "GST Certificate",
          description: "Upload a copy of the Goods and Services Tax (GST) registration certificate issued in the name of the proprietor or proprietorship firm. This certificate verifies your business’s GSTIN and tax compliance status.",
          format: "Image (JPG, PNG)"
        },

        {
          title: "Bank Details - Cancelled Cheque of Proprietorship",
          description: "Upload a scanned copy of a cancelled cheque from the proprietorship’s bank account. The cheque should clearly display the account holder’s name (proprietor), account number, and IFSC code to facilitate bank verification and payment processing.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Address Proof",
          description: "Upload a valid document that confirms the registered business address of the proprietorship. Acceptable documents include utility bills, lease/rental agreements, property tax receipts, or government-issued registration certificates showing the business address.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "PF / ESI / PT Registration Certificate",
          description: "Upload valid registration certificates for Provident Fund (PF), Employees’ State Insurance (ESI), and Professional Tax (PT), if applicable to your proprietorship. These documents confirm your business’s compliance with labor and tax laws.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "FSSAI Certificate",
          description: "Upload a valid Food Safety and Standards Authority of India (FSSAI) registration or license certificate. This is mandatory for proprietorships involved in the manufacturing, processing, distribution, or sale of food products.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "MSME Certificate",
          description: "Upload a copy of your Micro, Small, and Medium Enterprises (MSME) registration certificate, also known as the Udyam Registration Certificate. This document certifies your business’s recognition under the MSME Act and can help avail government benefits.",
          format: "Image (JPG, PNG)"
        },

      ]
    },
    {
      sectionTitle: 'Documents List for Individuals as per VRF',
      documents: [
        {
          title: "Vendor Registration Form - VRF",
          description: "Upload the completed Vendor Registration Form (VRF) containing your personal and professional details. This form is required to initiate onboarding as an individual vendor and should include your full name, contact information, address, and nature of services provided.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Individual Name",
          description: "Upload a government-issued identity document that clearly displays your full legal name...",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Individual PAN No",
          description: "Upload a clear copy of your Permanent Account Number (PAN) card issued by the Income Tax Department...",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Bank Details – Cancelled Cheque (Compulsory)",
          description: "Upload a cancelled cheque from your personal bank account. The cheque must clearly show your name, account number, and IFSC code. This is mandatory for verifying your bank details and enabling payment processing.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Billing / Invoice – As per VRF",
          description: "Upload a sample invoice or billing document that matches the details provided in your Vendor Registration Form (VRF). This ensures consistency in vendor records, including name, address, GSTIN (if applicable), and bank details.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Address of the Individual",
          description: "Upload a valid proof of your current residential address. Acceptable documents include Aadhaar card, utility bills, bank statements, rental agreements, or any government-issued document showing your name and address.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "Aadhaar Card of the Individual",
          description: "Upload a clear copy of your Aadhaar card issued by UIDAI. This document is used for identity and address verification during the onboarding process.",
          format: "Image (JPG, PNG)"
        },
        {
          title: "GST Certificate – If Registered",
          description: "Upload your Goods and Services Tax (GST) registration certificate if you are a registered GST individual vendor. This certificate verifies your GSTIN and tax compliance. If not registered under GST, this document is not required.",
          format: "Image (JPG, PNG)"
        },

      ]
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(e, item) => item?.onClick?.()}
      />
      <Box>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h4" fontWeight="bold">
              Document Upload
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please upload all required documents for your onboarding process.
            </Typography>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Onboarding Progress
              </Typography>
              <Box sx={{ width: '100%', backgroundColor: '#eee', height: 10, borderRadius: 5, mt: 0.5 }}>
                <Box sx={{ width: '0%', backgroundColor: '#1976d2', height: '100%', borderRadius: 5 }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="caption" color="text.secondary">
                  0% Complete
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Box>
      <Box sx={{ p: 4 }}>
        {documentsData.map((section, sectionIndex) => (
          <Box key={sectionIndex} sx={{ mt: 4 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {section.sectionTitle}
            </Typography>
            {/* Onboarding Progress Bar */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Onboarding Progress
              </Typography>
              <Box sx={{ width: '100%', backgroundColor: '#eee', height: 10, borderRadius: 5, mt: 0.5 }}>
                <Box sx={{ width: '0%', backgroundColor: '#1976d2', height: '100%', borderRadius: 5 }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="caption" color="text.secondary">
                  0% Complete
                </Typography>
              </Box>
            </Box>
            {/* Document Items */}
            {section.documents.map((doc, docIndex) => (
              <DocumentItem
                key={`${sectionIndex}-${docIndex}`}
                title={doc.title}
                description={doc.description}
                required={doc.required}
                format={doc.format}
                onClick={() => handleOpenModal(doc.title)}
              />
            ))}

            {sectionIndex < documentsData.length - 1 && <Divider sx={{ my: 4 }} />}
          </Box>
        ))}
        <UploadModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          documentTitle={activeDoc}
          onUpload={handleUpload}
        />
      </Box>
    </Box>
  );
}
export default UpdateDocument;




